import React, { useState } from "react";
import { Brain, Check, ChevronDown, Eye, Lock, X } from "lucide-react";
import type { ModelOption } from "./types";

// ─── Icon Tooltip ────────────────────────────────────────────────

function IconTooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span className="cuadra-relative cuadra-inline-flex cuadra-group/tip">
      {children}
      <span
        className="cuadra-absolute cuadra-bottom-full cuadra-left-1/2 cuadra-mb-1.5 cuadra-hidden group-hover/tip:cuadra-flex cuadra-items-center cuadra-whitespace-nowrap cuadra-rounded-md cuadra-px-2 cuadra-py-1 cuadra-text-[10px] cuadra-pointer-events-none cuadra-z-50 cuadra-font-brand"
        style={{
          backgroundColor: "var(--cuadra-popover)",
          color: "var(--cuadra-foreground)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
          transform: "translateX(-50%)",
        }}
      >
        {label}
      </span>
    </span>
  );
}

// ─── Model Selector ──────────────────────────────────────────────

interface ModelSelectorProps {
  models: ModelOption[];
  selectedModelId?: string;
  onModelChange?: (modelId: string) => void;
  lockedLabel?: string;
}

export function ModelSelector({
  models,
  selectedModelId,
  onModelChange,
  lockedLabel = "Upgrade to use this model",
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedModel =
    models.find((m) => m.id === selectedModelId) || models[0];

  if (!selectedModel) return null;

  const handleModelSelect = (model: ModelOption) => {
    if (model.locked) return;
    setIsOpen(false);
    if (model.id !== selectedModelId) {
      onModelChange?.(model.id);
    }
  };

  return (
    <div className="cuadra-relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="cuadra-flex cuadra-items-center cuadra-gap-1.5 cuadra-px-2.5 cuadra-py-1.5 cuadra-rounded-lg cuadra-text-sm md:cuadra-text-base cuadra-text-muted-foreground hover:cuadra-text-foreground cuadra-transition-colors cuadra-font-brand"
      >
        <span>{selectedModel.name}</span>
        <ChevronDown className="cuadra-h-3.5 cuadra-w-3.5" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop — desktop only */}
          <div
            className="cuadra-hidden md:cuadra-block cuadra-fixed cuadra-inset-0 cuadra-z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Mobile: fullscreen overlay */}
          <div
            className="cuadra-fixed cuadra-inset-0 cuadra-z-50 cuadra-flex cuadra-flex-col md:cuadra-hidden"
            style={{ backgroundColor: "var(--cuadra-popover)" }}
          >
            <div className="cuadra-flex cuadra-items-center cuadra-justify-end cuadra-px-4 cuadra-py-3 cuadra-min-h-[48px]">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-p-1.5 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-bg-muted/60 cuadra-transition-colors"
              >
                <X className="cuadra-h-5 cuadra-w-5" />
              </button>
            </div>

            <div className="cuadra-flex-1 cuadra-min-h-0 cuadra-overflow-y-auto cuadra-py-1">
              {models.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  disabled={model.locked}
                  className={`cuadra-w-full cuadra-flex cuadra-items-center cuadra-justify-between cuadra-gap-3 cuadra-px-4 cuadra-py-3 cuadra-text-sm cuadra-text-left cuadra-transition-colors cuadra-border-b cuadra-border-border/40 last:cuadra-border-b-0 cuadra-font-brand ${model.locked ? "cuadra-opacity-50 cuadra-cursor-not-allowed" : "hover:cuadra-bg-muted/60"}`}
                  onClick={() => handleModelSelect(model)}
                >
                  <div className="cuadra-flex cuadra-flex-col cuadra-min-w-0">
                    <div className="cuadra-flex cuadra-items-center cuadra-gap-1.5">
                      <span className="cuadra-text-foreground cuadra-truncate">
                        {model.name}
                      </span>
                      {model.locked && (
                        <IconTooltip label={lockedLabel}>
                          <Lock className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                        </IconTooltip>
                      )}
                      {model.supportsReasoning && (
                        <IconTooltip label="Reasoning">
                          <Brain className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                        </IconTooltip>
                      )}
                      {model.supportsVision && (
                        <IconTooltip label="Vision">
                          <Eye className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                        </IconTooltip>
                      )}
                    </div>
                    {model.locked && (
                      <span className="cuadra-text-xs cuadra-text-muted-foreground">
                        {lockedLabel}
                      </span>
                    )}
                    {!model.locked && model.description && (
                      <span className="cuadra-text-xs cuadra-text-muted-foreground">
                        {model.description}
                      </span>
                    )}
                  </div>
                  {!model.locked && model.id === selectedModelId && (
                    <Check className="cuadra-h-4 cuadra-w-4 cuadra-text-primary cuadra-flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop: positioned dropdown */}
          <div
            className="cuadra-hidden md:cuadra-block cuadra-absolute cuadra-top-full cuadra-left-1/2 cuadra--translate-x-1/2 cuadra-mt-2 cuadra-z-50 cuadra-rounded-xl cuadra-min-w-[200px] cuadra-py-1"
            style={{
              backgroundColor: "var(--cuadra-popover)",
              boxShadow:
                "0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
            }}
          >
            {models.map((model) => (
              <button
                key={model.id}
                type="button"
                disabled={model.locked}
                className={`cuadra-w-full cuadra-flex cuadra-items-center cuadra-justify-between cuadra-gap-3 cuadra-px-3 cuadra-py-2 cuadra-text-sm cuadra-text-left cuadra-transition-colors cuadra-font-brand ${model.locked ? "cuadra-opacity-50 cuadra-cursor-not-allowed" : "hover:cuadra-bg-muted/60"}`}
                onClick={() => handleModelSelect(model)}
              >
                <div className="cuadra-flex cuadra-flex-col cuadra-min-w-0">
                  <div className="cuadra-flex cuadra-items-center cuadra-gap-1.5">
                    <span className="cuadra-text-foreground cuadra-truncate">
                      {model.name}
                    </span>
                    {model.locked && (
                      <IconTooltip label={lockedLabel}>
                        <Lock className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                      </IconTooltip>
                    )}
                    {model.supportsReasoning && (
                      <IconTooltip label="Reasoning">
                        <Brain className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                      </IconTooltip>
                    )}
                    {model.supportsVision && (
                      <IconTooltip label="Vision">
                        <Eye className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground cuadra-flex-shrink-0" />
                      </IconTooltip>
                    )}
                  </div>
                  {model.locked && (
                    <span className="cuadra-text-xs cuadra-text-muted-foreground">
                      {lockedLabel}
                    </span>
                  )}
                  {!model.locked && model.description && (
                    <span className="cuadra-text-xs cuadra-text-muted-foreground">
                      {model.description}
                    </span>
                  )}
                </div>
                {!model.locked && model.id === selectedModelId && (
                  <Check className="cuadra-h-4 cuadra-w-4 cuadra-text-primary cuadra-flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
